import type { CalendarEvent } from "@/types/events.ts";

/**
 * Stable FullCalendar event id. A "this and following" split re-mints the
 * provider ids (`gcalId`, `recurringEventId`), so keying tiles by those would
 * remount every instance (visible flicker). Recurring instances instead key by
 * `calendarId:originalStartTimeMs:seriesKey` — the original start and the
 * split-normalized iCalUID both survive a split, and distinct series have
 * distinct iCalUIDs, so two same-title, same-time series no longer collapse
 * onto one tile (BIG-6462). Non-recurring events (and masters without an
 * occurrence start) keep `gcalId`. The real source id always travels in
 * `extendedProps.gcalId`.
 */
export function fullCalendarEventId(
  event: Pick<
    CalendarEvent,
    "isRecurring" | "originalStartTimeMs" | "calendarId" | "summary" | "gcalId" | "externalEventId"
  >,
): string | undefined {
  if (event.isRecurring && event.originalStartTimeMs != null) {
    return `${event.calendarId}:${event.originalStartTimeMs}:${recurringSeriesKey(event)}`;
  }
  return event.gcalId;
}

/**
 * The iCalUID with Google's `_R{YYYYMMDD}T{HHMMSS}` split suffix stripped, so
 * the key is identical before and after a "this and following" split. The
 * suffix convention is Google's, so only `…@google.com` UIDs are normalized
 * (same normalization as the backend's `meetingEventIdentity`). Falls back to
 * `summary` when no iCalUID is present.
 */
function recurringSeriesKey(event: Pick<CalendarEvent, "externalEventId" | "summary">): string {
  const iCalUid = event.externalEventId;
  if (iCalUid != null && iCalUid !== "") {
    return iCalUid.replace(/(?:_R\d{8}T\d{6})+(?=@google\.com$)/, "");
  }
  return event.summary;
}
